import { useEffect } from 'react';

import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useNavigate, useParams } from 'react-router-dom';

import { contactsApi } from '@/lib/api/contacts.api';
import { useToolWatchStore } from '@/stores/tool-watch.store';

import type {
  ContactCleanupMode,
  ContactQuery,
  MergeContactsDto,
  UpdateContactDto,
} from '@repo/shared-types';

const QUERY_KEY = ['contacts'];
const DUPLICATES_KEY = ['contacts', 'duplicates'];
const CLEANUP_STATUS_KEY = ['contacts', 'cleanup-status'];
// Merging also rewrites conversations, so messaging caches must refresh too
const MESSAGING_KEYS = [['messaging-conversations'], ['messaging-messages']];

export const useContacts = (query: Partial<ContactQuery> = {}) =>
  useQuery({
    queryKey: [...QUERY_KEY, query],
    queryFn: () => contactsApi.getAll(query),
    placeholderData: keepPreviousData,
    refetchInterval: 30000,
  });

export const useContact = (id: string | undefined) =>
  useQuery({
    queryKey: [...QUERY_KEY, id],
    queryFn: () => contactsApi.getOne(id!),
    enabled: !!id,
    // A 404 means the contact was deleted — retrying will never succeed
    retry: (failureCount, error) =>
      !(isAxiosError(error) && error.response?.status === 404) && failureCount < 2,
  });

/** Older pages load on scroll-up; pages arrive newest-first from the API. */
export const useContactTimeline = (
  contactId: string | undefined,
  limit = 30,
  includeToolCalls = false,
) =>
  useInfiniteQuery({
    queryKey: [...QUERY_KEY, contactId, 'timeline', { includeToolCalls }],
    queryFn: ({ pageParam }) =>
      contactsApi.getTimeline(contactId!, {
        cursor: pageParam ?? undefined,
        limit,
        include_tool_calls: includeToolCalls,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.next_cursor,
    enabled: !!contactId,
    // Fallback only — real-time updates arrive via the messaging WebSocket,
    // which invalidates this query. A tight interval here multiplies by the
    // number of open multi-view cards, so keep it lazy.
    refetchInterval: 30000,
  });

/** Distinct contact tags for the contact-list tag filter. */
export const useContactTags = () =>
  useQuery({
    queryKey: [...QUERY_KEY, 'tags'],
    queryFn: () => contactsApi.getTags(),
    staleTime: 60000,
  });

export const useUpdateContact = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateContactDto }) =>
      contactsApi.update(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};

export const useDeleteContact = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => contactsApi.delete(id),
    // Purging also removes conversations + messages, so refresh messaging too.
    onSuccess: () => invalidateAfterMerge(qc),
  });
};

export const useContactDuplicates = (enabled = true) =>
  useQuery({
    queryKey: DUPLICATES_KEY,
    queryFn: () => contactsApi.getDuplicates(),
    enabled,
  });

export const invalidateAfterMerge = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: QUERY_KEY });
  for (const key of MESSAGING_KEYS) {
    qc.invalidateQueries({ queryKey: key });
  }
};

export const useMergeContacts = () => {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { contactId } = useParams<{ contactId: string }>();
  return useMutation({
    mutationFn: (data: MergeContactsDto) => contactsApi.merge(data),
    onSuccess: (result) => {
      invalidateAfterMerge(qc);
      // If the contact currently on screen was merged away, follow it to the
      // surviving primary instead of erroring on a deleted contact.
      if (contactId && result.merged_contact_ids.includes(contactId)) {
        navigate(`/sales-console/${result.primary_id}`, { replace: true });
      }
    },
  });
};

export const useStartContactCleanup = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (mode: ContactCleanupMode) => contactsApi.startCleanup(mode),
    onSuccess: () => qc.invalidateQueries({ queryKey: CLEANUP_STATUS_KEY }),
  });
};

/** Polls while a cleanup is running so the UI can show live progress. */
export const useContactCleanupStatus = (enabled = true) =>
  useQuery({
    queryKey: CLEANUP_STATUS_KEY,
    queryFn: () => contactsApi.getCleanupStatus(),
    enabled,
    refetchInterval: (query) => (query.state.data?.status === 'running' ? 1500 : false),
  });

/**
 * Fetches unacknowledged tool-watch alerts from the server on mount and
 * merges them into the Zustand store so contacts flash immediately.
 */
export const useToolWatchAlerts = () => {
  const mergeAlerts = useToolWatchStore((s) => s.mergeAlerts);
  const { data } = useQuery({
    queryKey: [...QUERY_KEY, 'tool-watch-alerts'],
    queryFn: () => contactsApi.getToolWatchAlerts(),
    staleTime: 30000,
  });

  useEffect(() => {
    if (data && data.length > 0) {
      mergeAlerts(data);
    }
  }, [data, mergeAlerts]);
};

/** Acknowledge all tool-watch alerts for a contact on the server. */
export const useAcknowledgeToolWatch = () =>
  useMutation({
    mutationFn: (contactId: string) => contactsApi.acknowledgeContactToolWatch(contactId),
  });
